var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SessionError, SessionHTTPError, SessionErrorKey } from '../error.mjs';
import withTimeoutSignal from './withTimeoutSignal.mjs';
/**
 * Http wrapper for Keycloak API.
 */
export class KeycloakHttpAPI {
    constructor(baseUrl, options) {
        this.baseUrl = baseUrl;
        this.options = options;
    }
    /**
     * Gets a JSON object from the specified URL.
     */
    get(path_1) {
        return __awaiter(this, arguments, void 0, function* (path, headers = {}) {
            const { data } = yield this.request(this.buildURL(path), {
                method: 'GET',
                headers: Object.assign(Object.assign({ Accept: 'application/json' }, this.buildDefaultHeaders()), headers),
            });
            return data;
        });
    }
    /**
     * Posts the specified object to the give URL as a url encoded body
     * and retrieves a JSON object.
     */
    post(path_1) {
        return __awaiter(this, arguments, void 0, function* (path, body = '', headers = {}, credentials) {
            const { data } = yield this.request(this.buildURL(path), {
                method: 'POST',
                body,
                headers: Object.assign(Object.assign(Object.assign({}, this.buildDefaultHeaders()), headers), { Accept: 'application/json' }),
                credentials: credentials || this.options.defaultRequestCredentials,
            });
            return data;
        });
    }
    /**
     * Thin wrapper over {@see fetch} that raises an error for any non 2xx status code.
     */
    request(requestURL, config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const context = {
                httpRequestURL: requestURL,
                httpRequestAccept: ((_a = config.headers) === null || _a === void 0 ? void 0 : _a.Accept) || null,
                httpRequestAcceptLanguage: ((_b = config.headers) === null || _b === void 0 ? void 0 : _b['Accept-Language']) || null,
                httpResponseStatus: null,
                httpResponseBody: null,
                httpResponseContentType: null,
                httpResponseWwwAuthenticateHeader: null,
            };
            let response = null;
            try {
                response = yield withTimeoutSignal(this.options.timeout, (signal) => __awaiter(this, void 0, void 0, function* () {
                    return fetch(requestURL, Object.assign(Object.assign({}, config), { signal }));
                }));
            }
            catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    throw new SessionError('Network request timed out', {
                        name: SessionErrorKey.TIMEOUT,
                        cause: error,
                        extra: context,
                    });
                }
                // fetch() throws TypError when the network connection is trouble
                // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#checking_that_the_fetch_was_successful
                if (error instanceof Error && error.name === 'TypeError') {
                    throw new SessionError('Failed to make network request', {
                        name: SessionErrorKey.NETWORK,
                        cause: error,
                        extra: context,
                    });
                }
                throw new SessionHTTPError('Failed to make network request', {
                    name: SessionErrorKey.REQUEST,
                    cause: error,
                    extra: context,
                });
            }
            const status = response.status;
            const contentType = response.headers.get('content-type');
            const wwwAuthenticateHeader = response.headers.get('www-authenticate');
            context.httpResponseStatus = status;
            context.httpResponseContentType = contentType;
            context.httpResponseWwwAuthenticateHeader = wwwAuthenticateHeader;
            let data = null;
            try {
                data = (yield this.parseResponseBody(response, contentType));
            }
            catch (error) {
                throw new SessionHTTPError('Failed to decode/parse body', {
                    name: SessionErrorKey.DECODE,
                    cause: error,
                    extra: context,
                });
            }
            context.httpResponseBody = data;
            if (status === 400) {
                throw new SessionHTTPError('Request input was invalid', {
                    name: SessionErrorKey.INPUT,
                    extra: context,
                });
            }
            if (status === 401) {
                const name = context.httpResponseWwwAuthenticateHeader
                    ? SessionErrorKey.WWW_AUTH_REQUIRED
                    : SessionErrorKey.AUTH_REQUIRED;
                throw new SessionHTTPError('Authentication required', {
                    name,
                    extra: context,
                });
            }
            if (status === 403) {
                throw new SessionHTTPError('Access denied', {
                    name: SessionErrorKey.ACCESS_DENIED,
                    extra: context,
                });
            }
            if (status === 404) {
                throw new SessionHTTPError('Not found', {
                    name: SessionErrorKey.NOT_FOUND,
                    extra: context,
                });
            }
            if (status === 409) {
                throw new SessionHTTPError('Conflict or duplicate', {
                    name: SessionErrorKey.CONFLICT,
                    extra: context,
                });
            }
            if (status === 410) {
                throw new SessionHTTPError('Resource no longer available', {
                    name: SessionErrorKey.GONE,
                    extra: context,
                });
            }
            if (status === 429) {
                throw new SessionHTTPError('Too Many Requests', {
                    name: SessionErrorKey.TOO_MANY_REQUESTS,
                    extra: context,
                });
            }
            if (status === 502 || status === 503) {
                throw new SessionHTTPError('Unknown server gateway error', {
                    name: SessionErrorKey.SERVER_GATEWAY,
                    extra: context,
                });
            }
            if (status >= 500) {
                throw new SessionHTTPError('Unknown server error', {
                    name: SessionErrorKey.SERVER,
                    extra: context,
                });
            }
            if (status < 200 || status > 300) {
                throw new SessionHTTPError('Server responded with an unknown error', {
                    name: SessionErrorKey.UNKNOWN,
                    extra: context,
                });
            }
            return { status, data };
        });
    }
    buildURL(path) {
        if (!this.baseUrl) {
            throw new SessionError('Base URL not set', { name: SessionErrorKey.UNKNOWN_SERVER });
        }
        return `${this.baseUrl}${path}`;
    }
    buildDefaultHeaders() {
        const headers = {};
        if (this.options.iso639S1LanguageCode) {
            headers['Accept-Language'] = this.options.iso639S1LanguageCode;
        }
        Object.assign(headers, this.options.defaultRequestHeaders);
        return headers;
    }
    parseResponseBody(response, contentType) {
        if (contentType === null || contentType === void 0 ? void 0 : contentType.includes('application/json')) {
            return response.json();
        }
        return response.text();
    }
    stringifyResponseBody(data) {
        if (!data) {
            return data;
        }
        if (typeof data === 'string') {
            return data;
        }
        try {
            return JSON.stringify(data);
        }
        catch (_error) {
            return String(data);
        }
    }
}
